import { ApiResponseError, CancelledError, MalformedResponseError, NetworkError, TimeoutError, } from './errors.js';
import { isApiErrorBody, } from './types/envelope.js';
/** How long a request may take before the client gives up. */
export const DEFAULT_TIMEOUT_MS = 15_000;
/** Statuses the HTTP contract says carry no body. */
const BODYLESS_STATUSES = new Set([204, 205, 304]);
export function createApiClient(options) {
    const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    const defaultTimeout = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    async function request(method, path, body, requestOptions = {}) {
        // A caller that cancelled before we got here — a screen that unmounted
        // while the token was being read — should not cause a network call at all.
        if (requestOptions.signal?.aborted) {
            throw new CancelledError();
        }
        const url = buildUrl(options.baseUrl, path, requestOptions.query);
        const timeoutMs = requestOptions.timeoutMs ?? defaultTimeout;
        // A timeout and a caller's cancellation are two independent reasons to
        // abort, and they must stay distinguishable: a timeout is an error the
        // user should see, a cancellation is not.
        const timeoutController = new AbortController();
        const timer = setTimeout(() => {
            timeoutController.abort();
        }, timeoutMs);
        const signals = [timeoutController.signal];
        if (requestOptions.signal) {
            signals.push(requestOptions.signal);
        }
        const headers = {
            Accept: 'application/json',
            ...(await resolveHeaders(options)),
            ...(requestOptions.headers ?? {}),
        };
        // `FormData` is passed to `fetch` untouched, and deliberately without a
        // `Content-Type`: the runtime has to set it itself, because only it knows
        // the multipart boundary it generated. Setting the header here — even to
        // the right media type — produces a body the server cannot parse, and the
        // failure looks like a validation error rather than a transport bug.
        const multipart = typeof FormData !== 'undefined' && body instanceof FormData;
        if (body !== undefined && !multipart) {
            headers['Content-Type'] = 'application/json';
        }
        // A credential the caller passed explicitly wins. Revoking a stale session
        // sends that session's token; if someone signed in meanwhile, replacing it
        // with theirs would revoke the wrong session.
        const explicit = Object.keys(headers).some((name) => name.toLowerCase() === 'authorization');
        const token = explicit ? null : await options.getToken?.();
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        try {
            const response = await doFetch(url, {
                method,
                headers,
                body: body === undefined ? undefined : multipart ? body : JSON.stringify(body),
                signal: anySignal(signals),
            });
            // The body is read **inside** the timeout window and inside this catch.
            // `fetch` resolves once headers arrive, so a stalled body stream would
            // otherwise hang forever with the timer already cleared, and a failure
            // reading it would escape as a raw error instead of a normalised one.
            return { status: response.status, payload: await readPayload(response) };
        }
        catch (cause) {
            // A MalformedResponseError from `readPayload` is already the answer; it
            // must not be reclassified as a transport failure.
            if (cause instanceof MalformedResponseError) {
                throw cause;
            }
            // The caller's own cancellation takes precedence: if the screen went
            // away, that is the truth regardless of what else fired.
            if (requestOptions.signal?.aborted) {
                throw new CancelledError();
            }
            if (timeoutController.signal.aborted) {
                throw new TimeoutError(timeoutMs);
            }
            throw new NetworkError(cause);
        }
        finally {
            clearTimeout(timer);
        }
    }
    async function send(method, path, body, requestOptions) {
        const { status, payload } = await request(method, path, body, requestOptions);
        if (status >= 200 && status < 300) {
            // Only a contractually bodyless status may have no body. A 200 or 201
            // that arrives empty — a truncated gateway response — is a failure, not
            // a success with an undefined result that blows up later when the caller
            // dereferences it.
            if (BODYLESS_STATUSES.has(status)) {
                return undefined;
            }
            if (payload === undefined) {
                throw new MalformedResponseError(status, 'the body is empty', '');
            }
            if (!isSuccessEnvelope(payload)) {
                throw new MalformedResponseError(status, 'the body has no `data` key', preview(payload));
            }
            return payload.data;
        }
        throw toResponseError(status, payload, options);
    }
    return {
        get: (path, requestOptions) => send('GET', path, undefined, requestOptions),
        async getPaginated(path, requestOptions) {
            const { status, payload } = await request('GET', path, undefined, requestOptions);
            if (status < 200 || status >= 300) {
                throw toResponseError(status, payload, options);
            }
            if (!isPaginatedEnvelope(payload)) {
                throw new MalformedResponseError(status, 'the body is not a paginated envelope', preview(payload));
            }
            const page = payload;
            return {
                items: page.data,
                meta: page.meta,
                links: page.links,
                // The **server's** answer where it gives one. `meta.has_more` is
                // served by the directory endpoints precisely so a client does not
                // have to work this out, and `links.next` remains the fallback for
                // every endpoint that predates it. Deriving it from
                // `page < last_page` in three screens is how one of them ends up
                // using `<=`.
                hasNextPage: page.meta.has_more ?? page.links.next !== null,
            };
        },
        post: (path, body, requestOptions) => send('POST', path, body ?? {}, requestOptions),
        patch: (path, body, requestOptions) => send('PATCH', path, body ?? {}, requestOptions),
        put: (path, body, requestOptions) => send('PUT', path, body ?? {}, requestOptions),
        delete: (path, body, requestOptions) => send('DELETE', path, body, requestOptions),
    };
}
// ---------------------------------------------------------------- internals
async function resolveHeaders(options) {
    return (await options.getHeaders?.()) ?? {};
}
function buildUrl(baseUrl, path, query) {
    const base = baseUrl.replace(/\/+$/, '');
    const suffix = path.startsWith('/') ? path : `/${path}`;
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query ?? {})) {
        // `undefined` means "not set"; sending it as the literal string
        // "undefined" is how a filter accidentally becomes a validation error.
        if (value !== undefined && value !== null) {
            search.append(key, String(value));
        }
    }
    const queryString = search.toString();
    return queryString ? `${base}${suffix}?${queryString}` : `${base}${suffix}`;
}
/**
 * `AbortSignal.any` where available, with a listener-based fallback.
 *
 * React Native's runtime does not always have `AbortSignal.any`.
 */
function anySignal(signals) {
    const [only] = signals;
    if (signals.length === 1 && only) {
        return only;
    }
    const withAny = AbortSignal;
    if (typeof withAny.any === 'function') {
        return withAny.any(signals);
    }
    const controller = new AbortController();
    for (const signal of signals) {
        if (signal.aborted) {
            controller.abort();
            break;
        }
        signal.addEventListener('abort', () => {
            controller.abort();
        });
    }
    return controller.signal;
}
async function readPayload(response) {
    const text = await response.text();
    if (text === '') {
        return undefined;
    }
    try {
        return JSON.parse(text);
    }
    catch {
        throw new MalformedResponseError(response.status, 'the body is not valid JSON', text.slice(0, 200));
    }
}
function isSuccessEnvelope(payload) {
    return typeof payload === 'object' && payload !== null && 'data' in payload;
}
/**
 * A paginated envelope, checked field by field.
 *
 * Accepting `{ data: [], meta: {}, links: {} }` merely because `meta` and
 * `links` are objects would leave `meta.total` undefined and make
 * `hasNextPage` **true** — because `undefined !== null` — sending a caller to
 * fetch a page that does not exist.
 */
function isPaginatedEnvelope(payload) {
    if (!isSuccessEnvelope(payload) || !Array.isArray(payload.data)) {
        return false;
    }
    const { meta, links } = payload;
    if (typeof meta !== 'object' || meta === null || typeof links !== 'object' || links === null) {
        return false;
    }
    const metaRecord = meta;
    const linksRecord = links;
    // `has_more` is optional — endpoints that predate it do not send one — but a
    // value that *is* present must be a boolean. `has_more: "false"` is truthy
    // in JavaScript, so accepting it would send an infinite scroll after a page
    // that does not exist, for ever.
    if ('has_more' in metaRecord && typeof metaRecord.has_more !== 'boolean') {
        return false;
    }
    const numeric = ['page', 'per_page', 'total', 'last_page'].every((key) => typeof metaRecord[key] === 'number' && Number.isFinite(metaRecord[key]));
    // A link is a URL or an explicit `null` at the ends — never absent, because
    // absent and "no further page" are different answers.
    const navigable = ['first', 'prev', 'next', 'last'].every((key) => key in linksRecord && (typeof linksRecord[key] === 'string' || linksRecord[key] === null));
    return numeric && navigable;
}
function toResponseError(status, payload, hooks) {
    if (!isApiErrorBody(payload)) {
        // A non-2xx that is not the documented envelope is a proxy page, a
        // gateway error or a misconfiguration — not something to guess at.
        return new MalformedResponseError(status, 'the body is not the documented error envelope', preview(payload));
    }
    const { code, message, details } = payload.error;
    if (code === 'UNAUTHENTICATED') {
        hooks.onUnauthenticated?.();
    }
    if (code === 'LEGAL_RECONSENT_REQUIRED') {
        hooks.onLegalReconsentRequired?.();
    }
    return new ApiResponseError(status, code, message, details, payload.meta?.request_id);
}
function preview(payload) {
    try {
        return JSON.stringify(payload).slice(0, 200);
    }
    catch {
        return String(payload);
    }
}
//# sourceMappingURL=client.js.map